/**
 * Header Store - manages header UI state, sticky behavior, scroll tracking, and element measurements.
 *
 * **Key responsibilities:**
 * - Calculate header height and margins for page layout
 * - Manage sticky header behavior with scroll detection
 * - Store refs to header sub-components (promo bar, navigation, utility)
 * - Provide height measurements for layout calculations
 *
 * **Use cases:**
 * - Sticky header on scroll
 * - Page content offset/margin calculation
 * - Transparent header on hero sections
 * - Mobile navigation expand/collapse
 * - Promo bar height for layout
 *
 * **Scroll behavior:**
 * - `showOnScrollUp`: Header appears when scrolling up, hides when scrolling down
 * - Debounced scroll detection (200ms debounce, 500ms maxWait)
 * - `pageScrolledUp` tracks scroll direction
 * - Sticky offset adjusts based on scroll state
 *
 * **Route-based behavior:**
 * - `excludeStickyRoute`: Routes where sticky header is disabled
 * - Auto-expand on page navigation (via `page:finish` hook)
 *
 * **Multi-brand notes:**
 * - All brands use same header store structure
 * - Brand-specific styling via component overrides
 * - Configuration per brand: `useAppConfig().components.vf.header`
 *
 * **Performance:**
 * - Uses `useElementBounding` for reactive element measurements
 * - Debounced scroll listeners prevent excessive re-renders
 * - Computed properties cached until dependencies change
 *
 * @see domains/core/components/vf/header for header component integration
 * @see CONTRIBUTING.md#vuenuxt-composables---critical-rules for setup-only composable rules
 */
export const useHeaderStore = defineStore('header', () => {
  const { excludeStickyRoute, showOnScrollUp }
    = useAppConfig().components.vf.header
  const router = useRouter()

  // ────────────────────────────────────────────────────────────────────────────
  // Refs (Component References)
  // ────────────────────────────────────────────────────────────────────────────

  /** Ref to main header element (used for height calculation) */
  const headerRef = ref()

  /** Ref to header content wrapper (used for transparent mode height) */
  const headerContentRef = ref()

  /** Ref to promo bar element (top banner) */
  const promoBarRef = ref()

  /** Ref to main navigation element */
  const navigationRef = ref()

  /** Ref to utility bar element (top-right icons: search, account, cart) */
  const utilityRef = ref()

  // ────────────────────────────────────────────────────────────────────────────
  // State
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Scroll direction tracking (up = true, down = false)
   * - Updated via debounced scroll listener
   * - Only active if `showOnScrollUp` config enabled
   */
  const pageScrolledUp = ref(false)

  /**
   * Clears scroll-direction state when beginning a new page navigation.
   *
   * Scroll direction describes the page that was left. Retaining it while
   * rendering new sticky elements makes them use the previous page's header
   * offset before their first scroll event.
   */
  function resetScrollDirection(): void {
    pageScrolledUp.value = false
  }

  /**
   * Is header transparent? (e.g., over hero image)
   * - true: Header background transparent
   * - false: Header has background color
   * - Affects height calculation (uses headerContent instead of header)
   */
  const transparent = ref(false)

  /**
   * Header color mode for contrast
   * - 'light': Dark text on light background
   * - 'dark': Light text on dark background (e.g., over dark hero)
   */
  const mode = ref<'light' | 'dark'>('light')

  // ────────────────────────────────────────────────────────────────────────────
  // Scroll Detection (Conditional)
  // ────────────────────────────────────────────────────────────────────────────

  if (showOnScrollUp) {
    const { directions } = useScroll(document)
    const { bottom, top } = toRefs(directions)

    // Scrolling up → show header
    watchDebounced(top, (value) => {
      if (value) pageScrolledUp.value = true
    }, { debounce: 200, maxWait: 500 })

    // Scrolling down → hide header
    watchDebounced(bottom, (value) => {
      if (value) pageScrolledUp.value = false
    }, { debounce: 200, maxWait: 500 })
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Element Bounds (Reactive Measurements)
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Reactive bounding box for each header element.
   * - Automatically updates when elements resize or scroll
   * - Provides: height, width, top, bottom, left, right
   */
  const headerBounds = useElementBounding(headerRef)
  const promoBarBounds = useElementBounding(promoBarRef)
  const navigationBounds = useElementBounding(navigationRef)
  const utilityBounds = useElementBounding(utilityRef)

  // ────────────────────────────────────────────────────────────────────────────
  // Computed Properties
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Should header be sticky on this route?
   * - Checks current route name against `excludeStickyRoute` config
   * - Example: checkout/cart pages may exclude sticky header
   */
  const isSticky = computed(() =>
    !excludeStickyRoute.some((prefix) => router.currentRoute.value.name?.toString().startsWith(prefix))
  )

  /**
   * Header height (responsive to transparent mode)
   * - Transparent: Uses headerContent height (inner wrapper)
   * - Normal: Uses full header height
   */
  const height = computed(() => transparent.value
    ? getOuterHeight(headerContentRef.value) || headerBounds.height.value
    : headerBounds.height.value)

  /**
   * Margin to add below header (for page content offset)
   * - Returns header bottom position if sticky
   * - Returns 0 if not sticky
   */
  const margin = computed(() => isSticky.value ? headerBounds.bottom.value - 1 || 0 : 0)

  /**
   * Offset for sticky header scroll reveal
   * - Returns margin if scrolled up (header visible)
   * - Returns 0 if scrolled down (header hidden)
   */
  const offset = computed(() => pageScrolledUp.value ? margin.value : 0)

  // ────────────────────────────────────────────────────────────────────────────
  // Lifecycle Hooks
  // ────────────────────────────────────────────────────────────────────────────

  // ────────────────────────────────────────────────────────────────────────────
  // Return (Public API)
  // ────────────────────────────────────────────────────────────────────────────

  return {
    /** Scroll direction tracking (true = scrolled up) */
    pageScrolledUp,

    /** Clears scroll direction when a new page navigation begins */
    resetScrollDirection,

    /** Is header transparent? (over hero, etc.) */
    transparent,

    /** Header color mode ('light' | 'dark') */
    mode,

    /**
     * Sticky header state and calculations
     */
    sticky: {
      /** Should header be sticky on current route? */
      active: isSticky,

      /** Margin to add below header for content offset */
      margin,

      /** Offset for scroll reveal (0 = hidden, margin = visible) */
      offset
    },

    /**
     * Template refs for header sub-components.
     * **Usage:** Set these refs in template to enable measurements.
     *
     * @example
     * ```vue
     * <template>
     *   <header :ref="headerStore.refs.header">
     *     <div :ref="headerStore.refs.navigation">Nav</div>
     *   </header>
     * </template>
     * ```
     */
    refs: {
      header: headerRef,
      headerContent: headerContentRef,
      navigation: navigationRef,
      promoBar: promoBarRef,
      utility: utilityRef
    },

    /**
     * Height measurements for all header elements.
     * **Reactive:** Updates when elements resize or page scrolls.
     *
     * **Use cases:**
     * - Page content offset calculation
     * - Scroll anchor adjustment
     * - Sticky header margin
     */
    height: {
      /** Full header height including promo bar */
      full: computed(() => height.value + promoBarBounds.height.value),

      /** Main header element height (reactive) */
      header: headerBounds.height,

      /** Navigation bar height (reactive) */
      navigation: navigationBounds.height,

      /** Promo bar height (reactive) */
      promoBar: promoBarBounds.height,

      /** Utility bar height (reactive) */
      utility: utilityBounds.height
    }
  }
})
